import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
} from "../../lib/emsApproval";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/GuidedRegisters.css";
import {
  createObjectivesTargetsRegisterItemApi,
  decideEmsApprovalApi,
  deleteObjectivesTargetsRegisterItemApi,
  downloadObjectivesTargetsRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchObjectivesTargetsRegisterApi,
  requestEmsApprovalApi,
  updateObjectivesTargetsRegisterItemApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "objectives_targets";
const COMPLETION_STATUSES = ["achieved", "missed", "cancelled"];
const STATUS_OPTIONS = ["planned", "in_progress", "achieved", "missed", "cancelled"];

const EMPTY_FORM = {
  iso_clause: "",
  title: "",
  objective_area: "",
  related_context: "",
  metric: "",
  baseline_value: "",
  target_value: "",
  target_date: "",
  owner: "",
  status: "planned",
  progress_percent: "",
  linked_action: "",
  evidence: "",
  notes: "",
};

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function ObjectivesTargets() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [defaultIsoClause, setDefaultIsoClause] = useState("6");
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [form, setForm] = useState({ ...EMPTY_FORM, iso_clause: "6" });

  const role = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(role);
  const canApprove = approveRoles.includes(role) && canApproveEmsModule("objectives_targets");
  const canReview = reviewRoles.includes(role);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchObjectivesTargetsRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load objectives and targets");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const modulePolicy = (payload?.policy || []).find((row) => String(row?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options) ? modulePolicy.iso_clause_options.map(String) : ["6"];
      const fallback = String(modulePolicy?.iso_clause_default || options[0] || "6");
      setDefaultIsoClause(fallback);
      setRequestRoles((modulePolicy?.request_roles || []).map((x) => String(x).toLowerCase()));
      setApproveRoles((modulePolicy?.approve_roles || []).map((x) => String(x).toLowerCase()));
      setReviewRoles((modulePolicy?.review_roles || []).map((x) => String(x).toLowerCase()));
      setWorkflowMode(String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase());
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallback }));
    } catch {
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "6" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const summary = useMemo(() => {
    const total = items.length;
    const complete = items.filter((item) => COMPLETION_STATUSES.includes(String(item?.status || "").toLowerCase())).length;
    const pendingApproval = items.filter((item) => isApprovalPending(item) || isApprovalPendingReview(item)).length;
    return [
      { label: "Total objectives", value: total },
      { label: "Complete", value: complete },
      { label: "Open", value: total - complete },
      { label: "Pending approval", value: pendingApproval },
    ];
  }, [items]);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      title: item.title || "",
      objective_area: item.objective_area || "",
      metric: item.metric || "",
      baseline_value: item.baseline_value || "",
      target_value: item.target_value || "",
      target_date: item.target_date || "",
      owner: item.owner || "",
      status: item.status || "planned",
      progress_percent: item.progress_percent || "",
      linked_action: item.linked_action || "",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.title || "").trim() || !String(form.objective_area || "").trim() || !String(form.metric || "").trim()) {
      toast.error("Title, objective area and metric are required");
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await updateObjectivesTargetsRegisterItemApi(editingId, form);
        toast.success("Objective updated");
      } else {
        await createObjectivesTargetsRegisterItemApi(form);
        toast.success("Objective created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save objective");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this objective?")) return;
    try {
      setDeletingId(id);
      await deleteObjectivesTargetsRegisterItemApi(id);
      toast.success("Objective deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete objective");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({ module_key: MODULE_KEY, item_id: item.id, notes });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({ module_key: MODULE_KEY, item_id: item.id, decision, notes });
      toast.success(`Objective ${decision}d`);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision} objective`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await downloadObjectivesTargetsRegisterCsvApi();
      downloadBlob(blob, "objectives_targets_register.csv");
    } catch (err) {
      toast.error(err?.message || "Failed to export objectives register");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="dashboard-page guided-register-page">
      <div className="dashboard-header">
        <div>
          <p className="guided-register-eyebrow">ISO 14001 planning</p>
          <h1>Objectives & Targets</h1>
          <p className="subtitle">Turn environmental priorities into measurable objectives with a named owner, target date, progress record and supporting evidence.</p>
        </div>
        <button className="btn-secondary" type="button" onClick={handleExport} disabled={exporting || loading}>
          {exporting ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      <div className="summary-row">
        {summary.map((item) => (
          <div className="kpi-card" key={item.label}>
            <div className="kpi-label">{item.label}</div>
            <h2>{loading ? "-" : item.value}</h2>
          </div>
        ))}
      </div>

      <section className="card guided-register-guidance">
        <div>
          <p className="guided-register-eyebrow">How to set an objective</p>
          <h3>Make the outcome specific, measurable and owned</h3>
          <p>Choose an improvement priority, define how progress will be measured, record the starting point and set a realistic target date.</p>
        </div>
        <div className="guided-register-guidance-grid">
          <div><strong>Outcome</strong><span>State the improvement you want to achieve, such as reducing office electricity use.</span></div>
          <div><strong>Measure</strong><span>Use one metric, baseline and target so progress can be compared consistently.</span></div>
          <div><strong>Delivery</strong><span>Assign an owner, actions, target date and evidence to show the result.</span></div>
        </div>
      </section>
      <div className="card settings-card guided-register-form">
        <div className="settings-header">
          <div><p className="guided-register-eyebrow">Your objective</p><h3>{editingId ? "Edit objective" : "Create objective"}</h3></div>
          <span className="kpi-sub">Set one measurable improvement outcome and update progress throughout delivery.</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} />
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving || loading}>
                {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
              <small>Use planned while work is being prepared, active while delivery is under way, and complete only when the target has been met or formally closed.</small>
            </label>
            <label className="settings-field">
              <span>Title</span>
              <input placeholder="e.g. Reduce office electricity consumption" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={saving || loading} />
              <small>Describe the improvement outcome in a short, clear statement.</small>
            </label>
            <label className="settings-field">
              <span>Objective area</span>
              <input placeholder="e.g. Energy and carbon" value={form.objective_area} onChange={(e) => setField("objective_area", e.target.value)} disabled={saving || loading} />
              <small>Name the environmental topic, aspect or compliance improvement this objective supports.</small>
            </label>
            <label className="settings-field">
              <span>Related Context issue / condition</span>
              <input placeholder="e.g. Resource-efficiency expectations from clients and suppliers" value={form.related_context} onChange={(e) => setField("related_context", e.target.value)} disabled={saving || loading} />
              <small>Optional: state the Context issue, condition, stakeholder expectation or business decision that this objective responds to.</small>
            </label>
            <label className="settings-field">
              <span>Metric</span>
              <input placeholder="e.g. Electricity use (kWh)" value={form.metric} onChange={(e) => setField("metric", e.target.value)} disabled={saving || loading} />
              <small>Use the unit or measure that will show progress consistently.</small>
            </label>
            <label className="settings-field">
              <span>Owner</span>
              <input placeholder="e.g. Facilities Manager" value={form.owner} onChange={(e) => setField("owner", e.target.value)} disabled={saving || loading} />
              <small>Name the person or role accountable for delivery and progress updates.</small>
            </label>
            <label className="settings-field">
              <span>Baseline value</span>
              <input placeholder="e.g. 120,000 kWh in 2025" value={form.baseline_value} onChange={(e) => setField("baseline_value", e.target.value)} disabled={saving || loading} />
              <small>Record the reliable starting value and period you will compare against.</small>
            </label>
            <label className="settings-field">
              <span>Target value</span>
              <input placeholder="e.g. Reduce by 10% by December 2027" value={form.target_value} onChange={(e) => setField("target_value", e.target.value)} disabled={saving || loading} />
              <small>State the intended measurable result, including the time period if relevant.</small>
            </label>
            <label className="settings-field">
              <span>Target date</span>
              <input type="date" value={form.target_date} onChange={(e) => setField("target_date", e.target.value)} disabled={saving || loading} />
              <small>Set the date by which the target should be achieved or formally reviewed.</small>
            </label>
            <label className="settings-field">
              <span>Progress %</span>
              <input placeholder="e.g. 35" value={form.progress_percent} onChange={(e) => setField("progress_percent", e.target.value)} disabled={saving || loading} />
              <small>Enter current delivery progress from 0 to 100, based on evidence.</small>
            </label>
            <label className="settings-field">
              <span>Linked action</span>
              <input placeholder="e.g. Complete LED lighting upgrade" value={form.linked_action} onChange={(e) => setField("linked_action", e.target.value)} disabled={saving || loading} />
              <small>Reference the practical action or project that will help achieve this objective.</small>
            </label>
            <EvidenceReferenceField
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving || loading}
              helpText="Link meter data, action records, invoices, project evidence or reports that demonstrate progress."
            />
            <label className="settings-field guided-register-wide-field">
              <span>Notes</span>
              <textarea rows={3} placeholder="Optional: record milestones, barriers, corrective actions or reasons for any missed target." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving || loading} />
              <small>Use this space to explain progress, changes to the plan, barriers or management decisions.</small>
            </label>
          </div>
          <div className="settings-actions">
            <button className="btn-primary" type="submit" disabled={saving || loading}>
              {saving ? "Saving..." : editingId ? "Update objective" : "Create objective"}
            </button>
            {editingId ? (
              <button className="btn-secondary" type="button" onClick={resetForm} disabled={saving}>
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Area</th>
              <th>Status</th>
              <th>Target date</th>
              <th>Owner</th>
              <th>Approval</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="table-empty">Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="table-empty">No objectives yet.</td></tr>
            ) : (
              items.map((item) => {
                const requestable = canRequestApprovalForRecord(item, canRequest, COMPLETION_STATUSES);
                return (
                  <tr key={item.id}>
                    <td>{item.title || "-"}</td>
                    <td>{item.objective_area || "-"}</td>
                    <td>{item.status || "-"}</td>
                    <td>{item.target_date || "-"}</td>
                    <td>{item.owner || "-"}</td>
                    <td>{item.approval_status || "not_requested"}</td>
                    <td className="table-actions">
                      <button className="btn-small" type="button" onClick={() => startEdit(item)}>Edit</button>
                      <button className="btn-small danger" type="button" onClick={() => handleDelete(item.id)} disabled={deletingId === item.id}>
                        {deletingId === item.id ? "Deleting..." : "Delete"}
                      </button>
                      {requestable ? (
                        <button className="btn-small" type="button" onClick={() => handleRequestApproval(item)} disabled={approvalBusyId === item.id}>
                          Request approval
                        </button>
                      ) : null}
                      {workflowMode === "two_step" && canReview && isApprovalPendingReview(item) ? (
                        <button className="btn-small" type="button" onClick={() => handleDecision(item, "review")} disabled={approvalBusyId === item.id}>
                          Review
                        </button>
                      ) : null}
                      {canApprove && isApprovalPending(item) ? (
                        <>
                          <button className="btn-small" type="button" onClick={() => handleDecision(item, "approve")} disabled={approvalBusyId === item.id}>
                            Approve
                          </button>
                          <button className="btn-small danger" type="button" onClick={() => handleDecision(item, "reject")} disabled={approvalBusyId === item.id}>
                            Reject
                          </button>
                        </>
                      ) : null}
                      {isApprovalApproved(item) ? <span className="kpi-sub">Approved</span> : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
